import { useEffect, useRef } from 'react';

/**
 * Calls ``onFinished`` when a chat turn that was running in
 * ``conversationId`` ends — the moment to pull whatever the agent saved.
 *
 * The turn boundary is one boolean owned by the stream, which survives
 * streaming, reconnects and a transcript reload, where a tool chip would not.
 * Pass ``isSending || isReconnecting`` (AIB-8117): a turn the chat attached to
 * mid-flight never sets ``isSending``, so without the reconnect flag its end
 * would have no rising edge. The latch records which conversation armed it, so
 * a turn cut short by a conversation swap is not claimed by its replacement.
 */
export function useTurnFinished(
  conversationId: string | null,
  turnRunning: boolean,
  onFinished: () => void,
): void {
  const armedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (turnRunning) {
      armedForRef.current = conversationId;
      return;
    }
    const armedFor = armedForRef.current;
    armedForRef.current = null;
    if (armedFor === null || armedFor !== conversationId) return;
    onFinished();
  }, [turnRunning, conversationId, onFinished]);
}
